import dayjs from 'dayjs';

// ── 유틸 ────────────────────────────────────────────────────────────

export const fmt = (date) => (date && dayjs(date).isValid() ? dayjs(date).format('MM/DD') : '');

export const dedupe = (arr) => {
    if (!Array.isArray(arr)) return arr;
    const seen = new Set();
    return arr.filter((item) => {
        const key =
            item != null && typeof item === 'object'
                ? item.id != null
                    ? `id:${item.id}`
                    : `name:${item.name ?? ''}`
                : `s:${String(item)}`;
        return seen.has(key) ? false : (seen.add(key), true);
    });
};

export const joinNames = (arr, sep = '/ ') =>
    dedupe(arr ?? [])
        .map((v) =>
            v != null && typeof v === 'object'
                ? v.name || v.code || String(Object.values(v)[0] ?? '')
                : String(v ?? ''),
        )
        .filter(Boolean)
        .join(sep);

// ── Volume 계산 ──────────────────────────────────────────────────────

/** Hazmat marker (OS-574). Appended after one space at the end of a quantity/weight group in the subject. */
const HAZ_SUFFIX = ' HAZ';

/**
 * Groups rows by key (container type / unit) in the order they first appear, which is the order shown
 * in the UI. With splitByHazmat, rows sharing a key are still counted separately when their hazmat flag
 * differs (OS-574): only the hazmat group gets the HAZ suffix, so a shipment mixing hazmat and regular
 * containers can list the same type twice, e.g. "7X20'GP/ 2X20'GP HAZ". Without it (Customs Clearance,
 * which never shows HAZ) same-key rows are merged so the reader does not see one type listed twice.
 */
const groupVolumeRows = (rows, getKey, accumulate, { splitByHazmat }) => {
    const groups = new Map();
    for (const row of rows) {
        const key = getKey(row);
        if (!key) continue;
        const hazmat = splitByHazmat && row.hazmat === true;
        const groupKey = `${key}\u0000${hazmat ? 1 : 0}`;
        if (!groups.has(groupKey)) groups.set(groupKey, { key, hazmat, acc: {} });
        accumulate(groups.get(groupKey).acc, row);
    }
    return [...groups.values()];
};

const formatVolumeGroups = (groups, render, sep) =>
    groups.map((g) => (g.hazmat ? `${render(g)}${HAZ_SUFFIX}` : render(g))).join(sep) || 'NO INFO';

/** { volume: with per-group HAZ suffix, volume_plain: hazmat ignored and same-key rows merged } */
const buildVolumePair = (rows, getKey, accumulate, render, sep) => ({
    volume: formatVolumeGroups(groupVolumeRows(rows, getKey, accumulate, { splitByHazmat: true }), render, sep),
    volume_plain: formatVolumeGroups(
        groupVolumeRows(rows, getKey, accumulate, { splitByHazmat: false }),
        render,
        sep,
    ),
});

/** Ocean: cntrList → "2X40'HC/ 1X45'RH" (hazmat group: "…/ 1X20'GP HAZ") */
const buildOceanVolume = (cntrList) =>
    buildVolumePair(
        cntrList,
        (c) => c.volume,
        (acc) => {
            acc.count = (acc.count || 0) + 1;
        },
        (g) => `${g.acc.count}X${g.key}`,
        '/ ',
    );

/** Air / Trucking: aliasList → "10PCS, 500KG" (hazmat group: "…, 500KG HAZ"). Unit groups are joined with '/ '. */
const buildAirVolume = (aliasList) =>
    buildVolumePair(
        aliasList,
        (a) => (a.quantity ? a.il_opus_volume_unit_id : null),
        (acc, a) => {
            acc.qty = (acc.qty || 0) + a.quantity;
            acc.weight = (acc.weight || 0) + (a.weight || 0);
        },
        (g) => `${g.acc.qty}${g.key}, ${g.acc.weight}KG`,
        '/ ',
    );

// ── HBL / CNTR 집계 ─────────────────────────────────────────────────

const collectHblNumbers = (hblList) => dedupe(hblList.map((hbl) => hbl.hbl || hbl.hawb || hbl.bol).filter(Boolean));

const collectCntrNumbers = (hblList) =>
    dedupe(hblList.flatMap((hbl) => (hbl.cntrList ?? []).map((c) => c.cntr_number)).filter(Boolean));

const collectReferences = (hblList) => dedupe(hblList.map((hbl) => hbl.reference).filter(Boolean));

// ── Delivery Location (Ocean Import) ────────────────────────────────

const locationName = (v) => (v != null && typeof v === 'object' ? v.name || v.code || '' : String(v ?? '')).trim();

/**
 * DELIVERY LOCATION display value for one STATUS UPDATE row (a container for Ocean, an alias for Air).
 *
 * With splits (aliases), join the aliases' Delivery To values with "/ ", showing each place once.
 * Without splits, or when every split's Delivery To is empty, fall back to the row's own Delivery To.
 */
export const formatDeliveryLocation = (row) => {
    // Delivery To is free text (Google Places), so treat case and inner-whitespace variants as the same place.
    // The first spelling seen is what gets displayed.
    const seen = new Set();
    const fromSplits = (row?.splitList ?? [])
        .map((sp) => locationName(sp.il_location_id_deliver_to))
        .filter(Boolean)
        .filter((name) => {
            const key = name.toLowerCase().replace(/\s+/g, ' ');
            return seen.has(key) ? false : (seen.add(key), true);
        });
    if (fromSplits.length > 0) return fromSplits.join('/ ');
    return locationName(row?.il_location_id_deliver_to);
};

// ── statusMessages getter ────────────────────────────────────────────

export const getStatusMessages = (tabValue, statusMessageList, houseId, childId) => {
    const visibleList = (statusMessageList ?? []).filter((msg) => !msg.hidden);

    if (tabValue === 4) {
        const filtered = visibleList.filter((msg) => msg.parent_id === houseId);
        const house = filtered.filter((m) => m.child_id === 0);
        const container = filtered
            .filter((msg) => msg.child_id === childId)
            .sort((a, b) => new Date(a.event_date) - new Date(b.event_date) || a.id - b.id);
        return [...house, ...container];
    }
    const filtered = visibleList.filter(
        (msg) => msg.house_id === houseId && (msg.child_id === 0 || msg.child_id === childId),
    );
    const house = filtered.filter((m) => m.child_id === 0);
    const container = filtered
        .filter((m) => m.child_id !== 0 && m.alias_id === 0)
        .sort((a, b) => new Date(a.event_date) - new Date(b.event_date) || a.id - b.id);
    const alias = filtered
        .filter((m) => m.child_id !== 0 && m.alias_id !== 0)
        .sort((a, b) => new Date(a.event_date) - new Date(b.event_date) || a.id - b.id);
    return [...house, ...container, ...alias];
};

// ── 메인 빌더 ────────────────────────────────────────────────────────

/**
 * shipmentList(nested) + statusMessageList → { rowInfo, containers }
 *
 * rowInfo    : shippingAdvice / preAlert / subject 렌더링에 사용
 * containers : statusUpdate 행 반복 렌더링에 사용
 */
export const buildEmailData = (shipmentList, statusMessageList, tabValue) => {
    if (!shipmentList?.id) return { rowInfo: {}, containers: [] };

    const main = shipmentList;
    const hblList = main.hblList ?? [];

    // ── FTL/LTL (tabValue 4) ─────────────────────────────────────────
    if (tabValue === 4) {
        const containers = hblList.map((bol) => ({
            ...bol,
            hbl_number: bol.bol || bol.hbl || '-',
            hbl_reference: bol.reference || '-',
            statusMessages: getStatusMessages(tabValue, statusMessageList, bol.il_ftl_ltl_id, bol.id),
        }));

        const { volume, volume_plain } = buildAirVolume(hblList);
        const rowInfo = {
            ...main,
            eta: fmt(main.eta),
            etd: fmt(main.etd),
            volume,
            volume_plain,
            quantity: volume,
            il_trade_partner_id: dedupe(hblList.map((b) => b.il_trade_partner_id?.name).filter(Boolean)).join('/ '),
            il_location_id_pick_up: dedupe([main.il_location_id_pick_up].flat().filter(Boolean)),
            il_location_id_deliver_to: dedupe([main.il_location_id_deliver_to].flat().filter(Boolean)),
            reference: collectReferences(hblList),
        };

        return { rowInfo, containers };
    }

    // ── Ocean Import (0) / Ocean Export (1) ──────────────────────────
    if (tabValue === 0 || tabValue === 1) {
        const hblOrderMap = Object.fromEntries(hblList.map((hbl, i) => [hbl.id, i]));
        const hblIdKey = tabValue === 0 ? 'il_ocean_import_hbl_id' : 'il_ocean_export_hbl_id';

        const containers = hblList
            .flatMap((hbl) =>
                (hbl.cntrList ?? []).map((cntr) => ({
                    ...cntr,
                    hbl_number: hbl.hbl || '-',
                    hbl_reference: hbl.reference || '-',
                    splitList: cntr.splitList ?? [],
                    delivery_location: formatDeliveryLocation(cntr),
                    statusMessages: getStatusMessages(tabValue, statusMessageList, hbl.id, cntr.id),
                })),
            )
            .sort((a, b) => {
                const oA = hblOrderMap[a[hblIdKey]] ?? Infinity;
                const oB = hblOrderMap[b[hblIdKey]] ?? Infinity;
                return oA - oB;
            });

        const allCntr = hblList.flatMap((hbl) => hbl.cntrList ?? []);
        const { volume, volume_plain } = buildOceanVolume(allCntr);

        const rowInfo = {
            ...main,
            eta: fmt(main.eta),
            etd: fmt(main.etd),
            feta: fmt(main.feta),
            deta: fmt(main.deta),
            volume,
            volume_plain,
            quantity: volume,
            hbl: collectHblNumbers(hblList),
            cntr_number: collectCntrNumbers(hblList),
            reference: collectReferences(hblList),
            il_location_id_deliver_to: dedupe(allCntr.map((c) => c.il_location_id_deliver_to).filter(Boolean)),
            il_trade_partner_id_shipper: dedupe(hblList.map((h) => h.il_trade_partner_id_shipper).filter(Boolean)),
            il_trade_partner_id_consignee: dedupe(hblList.map((h) => h.il_trade_partner_id_consignee).filter(Boolean)),
        };

        return { rowInfo, containers };
    }

    // ── Air Import (2) / Air Export (3) ──────────────────────────────
    const hblOrderMap = Object.fromEntries(hblList.map((hbl, i) => [hbl.id, i]));
    const hblIdKey = tabValue === 2 ? 'il_air_import_hbl_id' : 'il_air_export_hbl_id';

    const containers = hblList
        .flatMap((hbl) =>
            (hbl.cntrList ?? []).map((alias) => ({
                ...alias,
                hawb_number: hbl.hawb || hbl.hbl || '-',
                hbl_reference: hbl.reference || '-',
                delivery_location: formatDeliveryLocation(alias),
                statusMessages: getStatusMessages(tabValue, statusMessageList, hbl.id, alias.id),
            })),
        )
        .sort((a, b) => {
            const oA = hblOrderMap[a[hblIdKey]] ?? Infinity;
            const oB = hblOrderMap[b[hblIdKey]] ?? Infinity;
            return oA - oB;
        });

    const allAlias = hblList.flatMap((hbl) => hbl.cntrList ?? []);
    const { volume, volume_plain } = buildAirVolume(allAlias);

    const rowInfo = {
        ...main,
        eta: fmt(main.eta),
        etd: fmt(main.etd),
        feta: fmt(main.feta),
        deta: fmt(main.deta),
        volume,
        volume_plain,
        quantity: volume,
        hawb: collectHblNumbers(hblList),
        reference: collectReferences(hblList),
        il_location_id_deliver_to: dedupe(allAlias.map((a) => a.il_location_id_deliver_to).filter(Boolean)),
        il_trade_partner_id_shipper: dedupe(hblList.map((h) => h.il_trade_partner_id_shipper).filter(Boolean)),
        il_trade_partner_id_consignee: dedupe(hblList.map((h) => h.il_trade_partner_id_consignee).filter(Boolean)),
    };

    return { rowInfo, containers };
};
